import {
  CheckCircle as CheckCircleIcon,
  Layers as LayersIcon,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { IndicatorBase, IndicatorSelection } from "types";
import { CardMediaLoader } from "components/image/CardMediaLoader";
import { LayerCardDetails } from "components/layers/LayerCardDetails";
import { Spacer } from "components/layout/Spacer";
import { Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { LayerPreviewFallBack } from "components/boundary/LayerPreviewFallBack";

interface Props {
  indicator: IndicatorBase;
  selection: IndicatorSelection[];
  onAddToSelection: (indicator: IndicatorBase) => void;
  onRemoveFromSelection: (indicator: IndicatorBase) => void;
}

export function LayerCard(props: Props) {
  const { t } = useTranslation();

  const isSelected = useMemo(() => {
    return props.selection
      .map((item) => item.indicatorId)
      .includes(props.indicator.id);
  }, [props.selection, props.indicator]);

  function toggleIndicatorSelection() {
    if (isSelected) props.onRemoveFromSelection(props.indicator);
    else props.onAddToSelection(props.indicator);
  }

  return render();

  function render() {
    return (
      <StyledCard>
        <CardActionArea onClick={toggleIndicatorSelection}>
          <ErrorBoundary
            fallbackRender={(props) => <LayerPreviewFallBack {...props} />}
          >
            <Suspense
              fallback={
                <Skeleton variant="rectangular" height="140" width="100%" />
              }
            >
              <CardMediaLoader
                defaultIcon={<LayersIcon />}
                height="140"
                image={getImageUrl(props.indicator.image)}
                alt={props.indicator.title}
              />
            </Suspense>
          </ErrorBoundary>
          {isSelected && (
            <CheckCircleIcon className="active-indicator" fontSize="medium" />
          )}
          <CardContent>
            <Typography gutterBottom variant="subtitle1" className="title">
              {props.indicator.title}
            </Typography>

            <LayerCardDetails indicator={props.indicator} />
          </CardContent>
          <Spacer className="spacer" />
        </CardActionArea>
        <CardActions>
          {isSelected && (
            <Button
              size="small"
              color="error"
              onClick={toggleIndicatorSelection}
            >
              {t("label.remove")}
            </Button>
          )}
          {!isSelected && (
            <Button size="small" onClick={toggleIndicatorSelection}>
              {t("label.add")}
            </Button>
          )}
        </CardActions>
      </StyledCard>
    );
  }
}

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;

  .active-indicator {
    position: absolute;
    top: 140px;
    transform: translateY(-50%);
    right: ${({ theme }) => theme.spacing(2)};
    fill: ${({ theme }) => theme.palette.success.main};
  }

  .MuiCardActionArea-root {
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
  }

  .MuiCardActions-root {
    justify-content: flex-end;
  }

  .MuiCard-root {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
  }

  .MuiCardContent-root {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    width: 100%;

    .tag-list {
      margin-top: auto;
    }

    .layer-information:last-child {
      margin-bottom: ${({ theme }) => theme.spacing(4)};
    }
  }

  .title {
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }

  .text-start-adornment {
    display: flex;
    align-items: center;

    .MuiSvgIcon-root {
      margin-right: ${({ theme }) => theme.spacing(1)};
    }
  }

  .remove-min-width {
    min-width: unset;
  }
`;

function getImageUrl(url: string) {
  if (url.indexOf("/reflect") > -1) {
    return `${url}&height=140&transparent=true`;
  } else if (url) return url;

  return "";
}
